import { PlusCircleOutlined } from '@ant-design/icons';
import { Col, Row } from 'antd';
import { FormikProps } from 'formik';
import { isEmpty } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { doSetAccountApplicationCategoryViewList } from 'src/actions';
import { useAccountSelector } from 'src/hooks';
import { MButton } from 'src/lib';
import { AccountApplicationCategoryView, EntityAccountType } from 'src/models';
import { BaseStyle } from 'src/styles';
import { getAccountApplicationGovernmentIdCategoryTitle } from 'src/utils';

import { AccountApplicationCategoryDocument } from './AccountApplicationCategoryDocument';
import { AccountApplicationCategoryHeader } from './AccountApplicationCategoryHeader';
import { AccountApplicationFormValues } from './AccountApplicationForm';

interface AccountApplicationCategoryBodyProps {
  category: AccountApplicationCategoryView;
  entityAccountType?: EntityAccountType;
  form: FormikProps<AccountApplicationFormValues>;
}

export const AccountApplicationCategoryBody = ({
  category,
  form,
  entityAccountType,
}: AccountApplicationCategoryBodyProps) => {
  const categoryViewList: AccountApplicationCategoryView[] = useSelector(
    (state: any) => state.app.accountApplicationCategoryViewList,
  );

  const dispatch = useDispatch();
  const { account } = useAccountSelector();

  const isApplicationSubmitted = () => Boolean(account?.accountId);

  const isMultiDocumentUploadCategory = () => category.name.isGovernmentId;

  const canAddPerson = () => {
    return category.children?.every(child => !isEmpty(child.uploadedDocuments));
  };

  const onAddPerson = () => {
    dispatch(
      doSetAccountApplicationCategoryViewList(
        categoryViewList.map(anItem => {
          if (anItem.name.value === category.name.value && anItem.children) {
            return {
              ...anItem,
              children: [
                ...anItem?.children,
                {
                  key: `${category.name.value}-${anItem.children?.length + 1}`,
                  name: category.name,
                  isResolved: false,
                  requestedDocuments: category.requestedDocuments,
                  uploadedDocuments: [],
                },
              ],
            };
          }

          return anItem;
        }),
      ),
    );
  };

  if (isMultiDocumentUploadCategory()) {
    const baseTitle = getAccountApplicationGovernmentIdCategoryTitle({ entityAccountType });

    return (
      <Row>
        {category.children?.map((cat, index) => (
          <Col span={24} key={cat.key}>
            <AccountApplicationCategoryHeader
              title={`${baseTitle} #${index + 1}`}
              testId={`${baseTitle?.toLowerCase().replace(/[\/ ]/g, '-')}-${index + 1}`}
            />
            <AccountApplicationCategoryDocument
              label='Select the following document you plan to upload:'
              category={cat}
              form={form}
              accept={['image/png', 'image/jpeg', 'application/pdf']}
              title={baseTitle}
              testId={`${baseTitle?.toLowerCase().replace(/[\/ ]/g, '-')}-${index + 1}`}
            />
          </Col>
        ))}
        {!isApplicationSubmitted() && baseTitle && (
          <Col span={24} className={BaseStyle.level}>
            <MButton
              onClick={onAddPerson}
              type='secondary'
              disabled={!canAddPerson()}
              testId={`add-${baseTitle.toLowerCase().replace(/[\/ ]/g, '-')}-btn`}>
              <PlusCircleOutlined />
              <span>{`Add ${baseTitle}`}</span>
            </MButton>
          </Col>
        )}
      </Row>
    );
  }

  return (
    <AccountApplicationCategoryDocument
      category={category}
      form={form}
      isDeletable={false}
      testId={category.key.toLowerCase()}
    />
  );
};
